import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Zap, CheckCircle, UserPlus, Briefcase, Sparkles } from "lucide-react";

/**
 * Google OAuth Callback Handler - Branded Rekrutor Experience
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("connecting");
  const [statusText, setStatusText] = useState("Connecting to Google...");
  const [userName, setUserName] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [searchParams] = useSearchParams();

  // Status messages for the branded experience
  const statusMessages = {
    connecting: "Connecting to Google...",
    verifying: "Verifying your account...",
    creating: "Creating your Rekrutor profile...",
    welcome: "Welcome aboard!",
    loading: "Loading your dashboard...",
    error: "Something went wrong"
  };

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract code from URL query params (Google OAuth returns ?code=xxx)
      const code = searchParams.get("code");
      const error = searchParams.get("error");
      
      if (error) {
        console.error("Google OAuth error:", error);
        setStatus("error");
        setStatusText("Authentication was cancelled");
        await new Promise(resolve => setTimeout(resolve, 2000));
        navigate("/", { replace: true });
        return;
      }
      
      if (!code) {
        console.error("No code in URL params");
        setStatus("error");
        setStatusText("Invalid authentication response");
        await new Promise(resolve => setTimeout(resolve, 1500));
        navigate("/", { replace: true });
        return;
      }

      setStatus("verifying");
      setStatusText("Verifying your account...");

      try {
        // REMINDER: DO NOT HARDCODE THE URL - use window.location.origin
        const redirectUri = window.location.origin + "/auth/google";
        
        // Exchange code for session
        const response = await fetch(`${API}/auth/google/callback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ 
            code: code,
            redirect_uri: redirectUri
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Google auth callback failed:", errorData);
          throw new Error(errorData.detail || "Auth failed");
        }

        const data = await response.json();
        
        // Store session token in localStorage
        if (data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        
        // Clear the code from URL
        window.history.replaceState(null, "", window.location.pathname);
        
        // Set user name and check if new user
        setUserName(data.user?.name?.split(" ")[0] || "");
        setIsNewUser(data.is_new_user || false);
        
        if (data.is_new_user) {
          setStatus("creating");
          setStatusText("Creating your Rekrutor profile...");
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        
        setStatus("welcome");
        setStatusText(data.is_new_user ? "Welcome to Rekrutor!" : `Welcome back, ${data.user?.name?.split(" ")[0] || ""}!`);
        
        // Set user in context
        setUser(data.user);
        
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        setStatus("loading");
        setStatusText("Loading your dashboard...");
        
        await new Promise(resolve => setTimeout(resolve, 800));

        // Determine redirect destination
        let destination;
        if (!data.user.role) {
          destination = "/role-selection";
        } else if (data.user.role === "recruiter") {
          destination = "/dashboard/recruiter";
        } else {
          destination = "/dashboard/jobseeker";
        }
        
        // Use window.location for reliable navigation after OAuth
        window.location.href = destination;
      } catch (error) {
        console.error("Google auth error:", error);
        setStatus("error");
        setStatusText("Authentication failed. Please try again.");
        await new Promise(resolve => setTimeout(resolve, 2000));
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser, searchParams]);

  return (
    <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-crimson/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-crimson/5 rounded-full blur-3xl" />
      </div>
      
      {/* Main content */}
      <motion.div 
        className="relative z-10 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo with pulse animation */}
        <motion.div 
          className="relative inline-block mb-8"
          animate={{ scale: status === "welcome" ? [1, 1.1, 1] : 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className={`w-20 h-20 flex items-center justify-center transition-all duration-500 ${
            status === "error" ? "bg-red-500/20" : 
            status === "welcome" ? "bg-green-500/20" : "bg-crimson/20"
          }`}>
            <AnimatePresence mode="wait">
              {status === "welcome" ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </motion.div>
              ) : status === "error" ? (
                <motion.div
                  key="error"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <Zap className="w-10 h-10 text-red-400" />
                </motion.div>
              ) : (
                <motion.div
                  key="logo"
                  animate={{ rotate: status === "verifying" || status === "creating" ? 360 : 0 }}
                  transition={{ duration: 2, repeat: status === "verifying" || status === "creating" ? Infinity : 0, ease: "linear" }}
                >
                  <Zap className="w-10 h-10 text-crimson" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Pulse ring effect */}
          {(status === "connecting" || status === "verifying" || status === "creating") && (
            <motion.div
              className="absolute inset-0 border-2 border-crimson/30"
              animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
        </motion.div>
        
        {/* Brand name */}
        <motion.h1 
          className="font-heading text-3xl font-bold mb-2 tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          REKRUTOR
        </motion.h1>
        
        {/* Status text */}
        <AnimatePresence mode="wait">
          <motion.p
            key={statusText}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`text-lg mb-6 ${
              status === "error" ? "text-red-400" :
              status === "welcome" ? "text-green-400" : "text-gray-400"
            }`}
          >
            {statusText}
          </motion.p>
        </AnimatePresence>
        
        {/* Progress bar */}
        <div className="w-64 h-1 bg-[#222] overflow-hidden mx-auto">
          <motion.div 
            className={`h-full ${status === "error" ? "bg-red-500" : status === "welcome" ? "bg-green-500" : "bg-crimson"}`}
            initial={{ width: "0%" }}
            animate={{ 
              width: status === "connecting" ? "20%" :
                     status === "verifying" ? "45%" :
                     status === "creating" ? "65%" :
                     status === "welcome" ? "85%" :
                     status === "loading" ? "100%" :
                     status === "error" ? "100%" : "0%"
            }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        
        {/* New user welcome features */}
        <AnimatePresence>
          {isNewUser && status === "welcome" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.3 }}
              className="mt-10 grid grid-cols-3 gap-6 max-w-md mx-auto"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-crimson/10 flex items-center justify-center mx-auto mb-2">
                  <UserPlus className="w-6 h-6 text-crimson" />
                </div>
                <p className="text-xs text-gray-500">Profile Created</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-crimson/10 flex items-center justify-center mx-auto mb-2">
                  <Briefcase className="w-6 h-6 text-crimson" />
                </div>
                <p className="text-xs text-gray-500">Jobs Ready</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-crimson/10 flex items-center justify-center mx-auto mb-2">
                  <Sparkles className="w-6 h-6 text-crimson" />
                </div>
                <p className="text-xs text-gray-500">AI Enabled</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Footer */}
      <motion.p 
        className="absolute bottom-8 text-xs text-gray-600"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        AI-Powered Recruitment Platform
      </motion.p>
    </div>
  );
};

export default GoogleAuthCallback;
